import React, { useState, useEffect } from 'react'
import client from '@/api/client'
import { PageHeader } from '@/components/ui/PageHeader'
import { TapStageSla } from '@/types/index'
import { AlertCircle, Save } from 'lucide-react'

const stageLabels: Record<string, string> = {
  Review: 'Review',
  PhoneScreen: 'Phone Screen',
  Interview: 'Interview',
  Assessment: 'Assessment',
  ReferenceCheck: 'Reference Check',
  Offer: 'Offer',
  Outcome: 'Outcome',
}

// Backend returns a paginated StageSlaListResponse with snake_case fields;
// unwrap + map once so the component reads camelCase everywhere.
interface RawSla {
  id: string
  stage: string
  warning_days: number
  breach_days: number
  is_enabled: boolean
}

interface RawSlaList {
  items?: RawSla[]
  total?: number
}

const mapSla = (s: RawSla): TapStageSla => ({
  id: s.id,
  stage: s.stage,
  warningDays: s.warning_days,
  breachDays: s.breach_days,
  isEnabled: Boolean(s.is_enabled),
})

interface SlaSettingsProps {
  embedded?: boolean
}

const SlaSettings: React.FC<SlaSettingsProps> = ({ embedded = false }) => {
  const [slaSettings, setSlaSettings] = useState<TapStageSla[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)

  const fetchSettings = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await client.get<RawSlaList>('/tap/settings/stage-sla')
      const items = response.data?.items ?? []
      setSlaSettings(items.map(mapSla))
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch SLA settings'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchSettings()
  }, [])

  const handleFieldChange = (id: string, field: keyof TapStageSla, value: number | boolean) => {
    setSlaSettings((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value } : s))
    )
    setSaveSuccess(false)
  }

  // Backend exposes PATCH /tap/settings/stage-sla/{stage} (per-stage) rather
  // than a bulk PUT. Fan out one PATCH per row using snake_case field names
  // that the StageSlaUpdate schema expects.
  const handleSave = async () => {
    setSaving(true)
    setSaveSuccess(false)
    try {
      await Promise.all(
        slaSettings.map((sla) =>
          client.patch(`/tap/settings/stage-sla/${encodeURIComponent(sla.stage)}`, {
            warning_days: sla.warningDays,
            breach_days: sla.breachDays,
            is_enabled: sla.isEnabled,
          })
        )
      )
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch {
      alert('Failed to save SLA settings')
    } finally {
      setSaving(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '80px',
    padding: '6px 10px',
    border: '1px solid var(--color-light-grey)',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'var(--font-family)',
    textAlign: 'center',
  }

  const thStyle: React.CSSProperties = {
    padding: '12px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--color-dark-grey)',
    textTransform: 'uppercase',
    borderBottom: '1px solid var(--color-light-grey)',
    backgroundColor: 'var(--color-light-grey)',
  }

  const tdStyle: React.CSSProperties = {
    padding: '12px',
    fontSize: '14px',
    color: 'var(--color-black)',
    borderBottom: '1px solid var(--color-light-grey)',
  }

  return (
    <div style={{ padding: '0' }}>
      {!embedded && (
        <PageHeader
          title="SLA Settings"
          subtitle="Configure warning and breach thresholds per stage"
          actions={
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {saveSuccess && (
                <span style={{ fontSize: '13px', color: '#059669', fontWeight: 500 }}>
                  Saved successfully
                </span>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--color-magenta)',
                  color: 'var(--color-white)',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-family)',
                  opacity: saving ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Save size={16} />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          }
        />
      )}
      {embedded && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 24px 0 24px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {saveSuccess && (
              <span style={{ fontSize: '13px', color: '#059669', fontWeight: 500 }}>
                Saved successfully
              </span>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '8px 16px',
                backgroundColor: 'var(--color-magenta)',
                color: 'var(--color-white)',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: saving ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family)',
                opacity: saving ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Save size={16} />
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      <div style={{ padding: '24px' }}>
        {error && (
          <div style={{
            backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px',
            padding: '12px 16px', marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'flex-start',
          }}>
            <AlertCircle size={20} style={{ color: '#dc2626', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{ margin: 0, color: '#dc2626', fontWeight: 500 }}>Error loading SLA settings</p>
              <p style={{ margin: '4px 0 0 0', color: '#991b1b', fontSize: '13px' }}>{error.message}</p>
            </div>
          </div>
        )}

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-dark-grey)' }}>
            Loading SLA settings...
          </div>
        ) : slaSettings.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-dark-grey)' }}>
            No SLA settings found. They will be created when the system is initialised.
          </div>
        ) : (
          <div style={{
            backgroundColor: 'var(--color-white)',
            border: '1px solid var(--color-light-grey)',
            borderRadius: '8px',
            overflow: 'hidden',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-family)' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Stage</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Warning (days)</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Breach (days)</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Enabled</th>
                </tr>
              </thead>
              <tbody>
                {slaSettings.map((sla) => (
                  <tr key={sla.id}>
                    <td style={{ ...tdStyle, fontWeight: 500 }}>
                      {stageLabels[sla.stage] || sla.stage}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <input
                        type="number"
                        min={0}
                        value={sla.warningDays}
                        onChange={(e) => handleFieldChange(sla.id, 'warningDays', Number(e.target.value))}
                        style={inputStyle}
                      />
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <input
                        type="number"
                        min={0}
                        value={sla.breachDays}
                        onChange={(e) => handleFieldChange(sla.id, 'breachDays', Number(e.target.value))}
                        style={inputStyle}
                      />
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={sla.isEnabled}
                        onChange={(e) => handleFieldChange(sla.id, 'isEnabled', e.target.checked)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: 'var(--color-magenta)' }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{
          marginTop: '16px',
          padding: '12px 16px',
          backgroundColor: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: '6px',
          fontSize: '13px',
          color: '#1e40af',
        }}>
          <strong>Note:</strong> Warning days triggers an orange SLA indicator. Breach days triggers a red SLA indicator.
          Changes take effect after saving and apply to all applications in the respective stage.
        </div>
      </div>
    </div>
  )
}

export default SlaSettings
